import React, { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import './StoreOwner.css';

// Socket connection
const socket = io(config.SOCKET_URL, config.SOCKET_OPTIONS);

function StoreOwner() {
  const [formData, setFormData] = useState({
    customerName: '',
    productName: '',
    amount: ''
  });
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    paidOrders: 0,
    pendingOrders: 0,
    failedOrders: 0,
    totalRevenue: 0
  });
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Helper to build API URL correctly
  const getApiUrl = (endpoint) => {
    const base = config.API_URL.replace(/\/+$/, '');
    return `${base}/api/${endpoint}`;
  };

  // Fetch payment links
  const fetchPaymentLinks = useCallback(async () => {
    try {
      const url = getApiUrl('orders');
      console.log('📡 GET', url);
      
      const response = await axios.get(url, { 
        timeout: 15000,
        headers: { 'Accept': 'application/json' }
      });
      
      console.log('📦 Orders:', response.data);
      
      if (response.data.orders) {
        setPaymentLinks(response.data.orders);
        setError(null);
      } else if (response.data.success && response.data.orders) {
        setPaymentLinks(response.data.orders);
        setError(null);
      } else {
        setPaymentLinks([]);
      }
    } catch (err) {
      console.error('❌ Orders Error:', err.message);
      if (err.response) {
        setError(`Server error: ${err.response.status}`);
      } else if (err.request) {
        setError('Cannot connect to server');
      } else {
        setError(err.message);
      }
    }
  }, []);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const url = getApiUrl('statistics');
      console.log('📡 GET', url);
      
      const response = await axios.get(url, { 
        timeout: 15000,
        headers: { 'Accept': 'application/json' }
      });
      
      console.log('📊 Stats:', response.data);
      
      if (response.data.statistics) {
        setStats(response.data.statistics);
      } else if (response.data.success && response.data.statistics) {
        setStats(response.data.statistics);
      }
    } catch (err) {
      console.error('❌ Stats Error:', err.message);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const url = getApiUrl('notifications');
      const response = await axios.get(url, { timeout: 10000 });
      
      if (response.data.notifications) {
        setNotifications(response.data.notifications);
      }
    } catch (err) {
      console.error('❌ Notifications Error:', err.message);
    }
  }, []);

  // Load all data
  const loadAllData = useCallback(async () => {
    setError(null);
    setDataLoaded(false);
    try {
      await Promise.all([
        fetchPaymentLinks(),
        fetchStatistics(),
        fetchNotifications()
      ]);
      setDataLoaded(true);
    } catch (err) {
      console.error('Load Error:', err);
      setDataLoaded(true);
    }
  }, [fetchPaymentLinks, fetchStatistics, fetchNotifications]);

  // Socket events
  useEffect(() => {
    console.log('🚀 Dashboard Mounted');
    console.log('📍 API:', config.API_URL);
    console.log('📍 Socket:', config.SOCKET_URL);

    socket.on('connect', () => {
      console.log('✅ Socket Connected');
      setConnected(true);
      setError(null);
      socket.emit('joinStoreOwner');
    });

    socket.on('disconnect', () => {
      console.log('❌ Socket Disconnected');
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('🔌 Socket Error:', err.message);
      setConnected(false);
    });

    socket.on('paymentSuccess', (data) => {
      console.log('💰 Payment:', data);
      toast.success(`💰 Payment of ₹${data.amount} received!`);
      loadAllData();
    });

    socket.on('paymentLinkCreated', (data) => {
      toast.info(`🔗 Link created for ${data.customerName}`);
      loadAllData();
    });

    socket.on('initialData', (data) => {
      if (data.statistics) setStats(data.statistics);
    });

    loadAllData();

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('paymentSuccess');
      socket.off('paymentLinkCreated');
      socket.off('initialData');
    };
  }, [loadAllData]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.productName || !formData.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      const url = getApiUrl('create-payment-link');
      console.log('📡 POST', url, formData);
      
      const response = await axios.post(url, {
        customerName: formData.customerName,
        productName: formData.productName,
        amount: parseFloat(formData.amount)
      }, { 
        timeout: 15000,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('✅ Created:', response.data);

      if (response.data.success) {
        toast.success('✅ Payment link created!');
        setFormData({ customerName: '', productName: '', amount: '' });
        
        if (response.data.paymentLink) {
          try {
            await navigator.clipboard.writeText(response.data.paymentLink);
            toast.success('📋 Link copied!');
          } catch (err) {}
        }
        
        loadAllData();
      } else {
        toast.error(response.data.message || 'Failed');
      }
    } catch (err) {
      console.error('❌ Create Error:', err);
      toast.error(err.response?.data?.message || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Copy to clipboard
  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('📋 Copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Share via WhatsApp
  const shareWhatsApp = (link, name) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`Hi ${name}, your payment link: ${link}`)}`, '_blank');
  };

  // Filter links
  const filteredLinks = () => {
    if (!paymentLinks) return [];
    if (filter === 'ALL') return paymentLinks;
    return paymentLinks.filter(l => l.status === filter);
  };

  // Format currency
  const fmt = (amount) => '₹' + Number(amount || 0).toLocaleString('en-IN');

  // Format date
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  // Status style
  const statusStyle = (s) => {
    switch(s) {
      case 'PAID': return { bg: '#f0fdf4', c: '#166534', b: '#86efac', i: '✅' };
      case 'PENDING': return { bg: '#fffbeb', c: '#92400e', b: '#fcd34d', i: '⏳' };
      case 'FAILED': return { bg: '#fef2f2', c: '#991b1b', b: '#fca5a5', i: '❌' };
      default: return { bg: '#f9fafb', c: '#374151', b: '#d1d5db', i: '📋' };
    }
  };

  // Filter count
  const filterCount = (t) => {
    if (!paymentLinks) return 0;
    return t === 'ALL' ? paymentLinks.length : paymentLinks.filter(l => l.status === t).length;
  };

  return (
    <div className="store-owner-container">
      <div className="container">
        {/* Top Status Bar */}
        <div style={{
          background: 'white', padding: '10px 20px', borderRadius: '12px',
          marginBottom: '16px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', fontSize: '0.85rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: connected ? '#22c55e' : '#ef4444',
              display: 'inline-block'
            }}></span>
            <span style={{ color: '#666' }}>
              {connected ? 'Connected' : 'Connecting...'}
            </span>
            <span style={{ color: '#999' }}>|</span>
            <span style={{ color: '#666' }}>📋 {paymentLinks.length} orders</span>
            <span style={{ color: '#999' }}>|</span>
            <span style={{ color: '#666' }}>💰 {fmt(stats.totalRevenue)}</span>
          </div>
          <button onClick={loadAllData} style={{
            padding: '6px 16px', background: '#667eea', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: '500'
          }}>
            🔄 Refresh
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            padding: '12px 20px', borderRadius: '12px', marginBottom: '16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ color: '#991b1b', fontSize: '0.9rem' }}>⚠️ {error}</span>
            <button onClick={loadAllData} style={{
              padding: '6px 16px', background: '#991b1b', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}>Retry</button>
          </div>
        )}

        {/* Test Mode Banner */}
        {config.IS_TEST_MODE && (
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: 'white', padding: '8px 20px', borderRadius: '12px',
            marginBottom: '16px', fontSize: '0.85rem', textAlign: 'center'
          }}>
            🧪 Test Mode - No real payments processed
          </div>
        )}

        {/* Header */}
        <div className="header">
          <div className="header-content">
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1a1a2e' }}>
                🏪 {config.APP_NAME}
              </h1>
              <p style={{ color: '#666' }}>Create payment links & receive real-time notifications</p>
            </div>
            <div style={{
              padding: '8px 20px', borderRadius: '50px',
              background: connected ? '#f0fdf4' : '#fef2f2',
              color: connected ? '#166534' : '#991b1b',
              border: `1px solid ${connected ? '#86efac' : '#fca5a5'}`,
              fontWeight: 600, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: connected ? '#22c55e' : '#ef4444'
              }}></span>
              {connected ? 'Live' : 'Offline'}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          {[
            { icon: '📋', val: stats.totalOrders || 0, label: 'Total Orders', color: '#8b5cf6', bg: '#f3f0ff' },
            { icon: '✅', val: stats.paidOrders || 0, label: 'Paid', color: '#22c55e', bg: '#f0fdf4' },
            { icon: '⏳', val: stats.pendingOrders || 0, label: 'Pending', color: '#f59e0b', bg: '#fffbeb' },
            { icon: '💰', val: fmt(stats.totalRevenue || 0), label: 'Revenue', color: '#3b82f6', bg: '#eff6ff' }
          ].map((s, i) => (
            <div key={i} className="stat-card" style={{ borderLeft: `4px solid ${s.color}` }}>
              <div style={{
                width: '55px', height: '55px', borderRadius: '12px',
                background: s.bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '1.8rem'
              }}>
                {s.icon}
              </div>
              <div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1a2e' }}>{s.val}</h3>
                <p style={{ color: '#666', fontSize: '0.85rem' }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Grid */}
        <div className="content-grid">
          {/* Create Form */}
          <div className="card">
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '24px' }}>
              📝 Create Payment Link
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Customer Name *</label>
                <input type="text" name="customerName" value={formData.customerName}
                  onChange={handleChange} placeholder="Enter customer name" required />
              </div>
              <div className="form-group">
                <label>Product/Service *</label>
                <input type="text" name="productName" value={formData.productName}
                  onChange={handleChange} placeholder="Enter product name" required />
              </div>
              <div className="form-group">
                <label>Amount (₹) *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: '16px', top: '50%',
                    transform: 'translateY(-50%)', fontWeight: 700, color: '#374151'
                  }}>₹</span>
                  <input type="number" name="amount" value={formData.amount}
                    onChange={handleChange} placeholder="0.00" required
                    style={{ paddingLeft: '36px' }} />
                </div>
              </div>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating...' : '🔗 Generate Payment Link'}
              </button>
            </form>
          </div>

          {/* Payment Links */}
          <div className="card">
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px'
            }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>
                📋 Payment Links ({paymentLinks.length})
              </h2>
              <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '4px' }}>
                {['ALL', 'PENDING', 'PAID'].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{
                    padding: '8px 16px', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                    background: filter === f ? 'white' : 'transparent',
                    color: filter === f ? '#667eea' : '#666',
                    boxShadow: filter === f ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}>
                    {f} <span style={{
                      background: filter === f ? '#667eea' : '#e5e7eb',
                      color: filter === f ? 'white' : '#666',
                      padding: '2px 8px', borderRadius: '20px', fontSize: '0.75rem', marginLeft: '4px'
                    }}>{filterCount(f)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="payment-links-list">
              {!dataLoaded ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p style={{ marginTop: '16px', color: '#666' }}>Loading...</p>
                </div>
              ) : filteredLinks().length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                  <p>No payment links found</p>
                  <p style={{ fontSize: '0.85rem' }}>Create your first payment link!</p>
                </div>
              ) : (
                filteredLinks().map((link, idx) => {
                  const s = statusStyle(link.status);
                  return (
                    <div key={idx} className="payment-link-item"
                      style={{ borderLeft: `4px solid ${s.b}` }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: '12px'
                      }}>
                        <span style={{
                          background: s.bg, color: s.c, padding: '4px 12px',
                          borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700
                        }}>
                          {s.i} {link.status}
                        </span>
                        <span style={{ fontSize: '1.3rem', fontWeight: 700 }}>{fmt(link.amount)}</span>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <div className="detail-row">
                          <span className="detail-label">👤 Customer</span>
                          <span className="detail-value">{link.customerName || link.customer_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">📦 Product</span>
                          <span className="detail-value">{link.productName || link.product_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">📅 Date</span>
                          <span className="detail-value">{fmtDate(link.createdAt || link.created_at)}</span>
                        </div>
                      </div>
                      {link.paymentLink && (
                        <>
                          <div style={{
                            background: '#f9fafb', padding: '8px 12px',
                            borderRadius: '8px', marginBottom: '12px', border: '1px solid #e5e7eb'
                          }}>
                            <code style={{ fontSize: '0.75rem', color: '#666', wordBreak: 'break-all' }}>
                              {link.paymentLink}
                            </code>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => copyToClipboard(link.paymentLink)}
                              style={{
                                flex: 1, padding: '10px', border: 'none', borderRadius: '8px',
                                background: '#f3f4f6', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem'
                              }}>📋 Copy</button>
                            <a href={link.paymentLink} target="_blank" rel="noopener noreferrer"
                              style={{
                                flex: 1, padding: '10px', border: 'none', borderRadius: '8px',
                                background: '#667eea', color: 'white', textAlign: 'center',
                                textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem'
                              }}>🔗 Open</a>
                            <button onClick={() => shareWhatsApp(link.paymentLink, link.customerName || link.customer_name)}
                              style={{
                                padding: '10px 14px', border: 'none', borderRadius: '8px',
                                background: '#25D366', color: 'white', cursor: 'pointer', fontSize: '1.1rem'
                              }}>💬</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div className="card" style={{ marginTop: '24px' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '20px'
            }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>🔔 Notifications</h2>
              <button onClick={fetchNotifications} style={{
                padding: '6px 14px', background: '#f3f4f6', border: 'none',
                borderRadius: '8px', cursor: 'pointer'
              }}>🔄 Refresh</button>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {notifications.slice(0, 20).map((n, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '12px', padding: '12px',
                  borderBottom: '1px solid #f3f4f6',
                  background: n.is_read ? 'transparent' : '#f0f3ff',
                  borderLeft: n.is_read ? 'none' : '3px solid #667eea'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {n.type === 'PAYMENT_SUCCESS' ? '✅' : n.type === 'PAYMENT_FAILED' ? '❌' : '🔗'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '0.9rem', color: '#1a1a2e', marginBottom: '4px' }}>{n.message}</p>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>{fmtDate(n.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreOwner;